import React, { ChangeEvent, FC, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

interface Props {
  label: string;
  description?: string;
  value: string[] | undefined;
  onChange: (value: string[] | undefined) => void;
  shouldValidateNctFormat?: boolean;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
});

const NCT_ID_REGEX = /^NCT\d{8}(?:-[A-Za-z0-9]+)?$/;

const validateAndNormalizeNctIds = (
  values: string[],
): { error?: string; normalizedValues: string[] } => {
  const normalizedValues = values.map((v) => v.toUpperCase());
  const invalidValues = normalizedValues.filter(
    (v: string) => !NCT_ID_REGEX.test(v),
  );

  if (invalidValues.length > 0) {
    return {
      error: `Invalid NCT ID${
        invalidValues.length > 1 ? 's' : ''
      }: ${invalidValues.join(
        ', ',
      )}. Expected e.g. NCT00000000 or NCT00000000-LU1.`,
      normalizedValues: [],
    };
  }

  return { normalizedValues };
};

export const NctIdListCondition: FC<Props> = ({
  label,
  description,
  value,
  onChange,
  shouldValidateNctFormat = true,
}) => {
  const classes = useStyles();
  const [error, setError] = useState<string | undefined>(undefined);

  const handleChange = (_event: ChangeEvent<{}>, newValue: any) => {
    const rawValues = ((newValue as string[]) ?? []).map((v) => v.trim());
    const nonEmptyValues = rawValues.filter((v) => v.length > 0);

    if (shouldValidateNctFormat) {
      const { error: validationError, normalizedValues } =
        validateAndNormalizeNctIds(nonEmptyValues);

      if (validationError) {
        setError(validationError);
        onChange(undefined);
        return;
      }

      setError(undefined);
      onChange(normalizedValues.length ? normalizedValues : undefined);
      return;
    }

    setError(undefined);
    onChange(nonEmptyValues.length ? nonEmptyValues : undefined);
  };

  return (
    <div className={classes.root}>
      <Autocomplete
        freeSolo
        multiple
        options={[]}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(error)}
            helperText={error ?? description}
            label={label}
            variant="standard"
          />
        )}
        value={value ?? []}
        onChange={handleChange}
      />
    </div>
  );
};
